/**
 * TopicsPanel — Collapsible read-only topic list on the dashboard.
 *
 * Phase 1 surface for the Topic Curation system. Renders the case's topics
 * (title, status badge, priority chip, one-line hypothesis snippet) inside
 * a collapsible Card. Read-only — Phase 2 will add a topic detail view.
 *
 * The parent page should mount this only when there's at least one topic
 * — when the case has none, the panel hides entirely (no empty state).
 *
 * Props:
 *   topics: Array<{
 *     topic_id: string,
 *     title: string,
 *     hypothesis: string,
 *     status: 'OPEN' | 'IN_PROGRESS' | 'RESOLVED' | 'DISMISSED' | 'BLOCKED',
 *     priority: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW',
 *     source?: string,
 *   }>
 *   discoveryStatus?: {
 *     state: 'idle' | 'processing',
 *     reasons?: {
 *       unbackfilled_topics: number,
 *       actionable_leads: number,
 *       geocode_pending: boolean,
 *     }
 *   }
 *     Optional. When provided, renders a small badge in the panel header
 *     showing whether discovery is still chewing on this case ("Discovery
 *     active") or has settled until the next upload ("Up to date").
 *     Computed server-side from the same four conditions discovery's
 *     run_discovery uses to self-requeue, so the chip can't drift from
 *     actual loop behavior. Omitted on legacy backends -> chip hides.
 */

import React, { useState } from 'react';
import { Card, Badge, SectionTitle } from '../../design-system';
import styles from './TopicsPanel.module.css';

const HYPOTHESIS_TRUNCATE = 140;

const STATUS_VARIANT = {
  OPEN: 'info',
  IN_PROGRESS: 'accent',
  RESOLVED: 'success',
  DISMISSED: 'default',
  BLOCKED: 'warning',
};

const STATUS_LABEL = {
  OPEN: 'Open',
  IN_PROGRESS: 'In progress',
  RESOLVED: 'Resolved',
  DISMISSED: 'Dismissed',
  BLOCKED: 'Blocked',
};

const PRIORITY_VARIANT = {
  CRITICAL: 'error',
  HIGH: 'warning',
  MEDIUM: 'info',
  LOW: 'default',
};

function truncate(value, max) {
  if (!value) return '';
  if (value.length <= max) return value;
  return value.slice(0, max - 1).trimEnd() + '…';
}

function ChevronIcon({ open }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`${styles.chevron} ${open ? styles.chevronOpen : ''}`}
      aria-hidden="true"
    >
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function DiscoveryStatusBadge({ status }) {
  if (!status || !status.state) return null;
  const isProcessing = status.state === 'processing';
  const reasons = status.reasons || {};
  // Tooltip shows which condition is keeping the loop alive — maps 1:1
  // to the four-condition self-requeue guard at the end of run_discovery
  // so we can debug a stuck loop straight from the UI.
  const tooltipParts = [];
  if (reasons.unbackfilled_topics) {
    tooltipParts.push(
      `${reasons.unbackfilled_topics} topic(s) still being investigated`);
  }
  if (reasons.actionable_leads) {
    tooltipParts.push(
      `${reasons.actionable_leads} lead(s) queued for the next pass`);
  }
  if (reasons.geocode_pending) {
    tooltipParts.push('Geocoding still resolving — more topics may surface');
  }
  const tooltip = isProcessing
    ? (tooltipParts.join(' · ') || 'Discovery still has work pending')
    : 'No discovery work pending — settled until the next upload';
  return (
    <span title={tooltip} className={styles.discoveryBadge}>
      <Badge variant={isProcessing ? 'info' : 'success'} size="sm">
        {isProcessing ? 'Discovery active' : 'Up to date'}
      </Badge>
    </span>
  );
}

function TopicsPanel({ topics = [], onTopicClick, discoveryStatus = null }) {
  const [open, setOpen] = useState(true);

  if (!topics || topics.length === 0) {
    return null;
  }

  const handleRowClick = (topic) => {
    if (typeof onTopicClick === 'function') {
      onTopicClick(topic.topic_id || topic.item_id, topic.title);
    }
  };

  return (
    <Card padding="md">
      <button
        type="button"
        className={styles.headerButton}
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
      >
        <SectionTitle
          actions={(
            <span className={styles.headerActions}>
              <DiscoveryStatusBadge status={discoveryStatus} />
              <Badge variant="default" size="sm">{topics.length}</Badge>
              <ChevronIcon open={open} />
            </span>
          )}
        >
          Topics
        </SectionTitle>
      </button>

      {open && (
        <ul className={styles.list}>
          {topics.map((topic) => {
            const isClickable = typeof onTopicClick === 'function';
            const RowEl = isClickable ? 'button' : 'div';
            return (
              <li key={topic.topic_id || topic.item_id} className={styles.rowItem}>
                <RowEl
                  type={isClickable ? 'button' : undefined}
                  className={`${styles.row} ${isClickable ? styles.rowClickable : ''}`}
                  onClick={isClickable ? () => handleRowClick(topic) : undefined}
                  aria-label={isClickable ? `View topic ${topic.title}` : undefined}
                >
                  <div className={styles.rowMain}>
                    <div className={styles.title}>{topic.title}</div>
                    <div className={styles.hypothesis}>
                      {truncate(topic.hypothesis, HYPOTHESIS_TRUNCATE)}
                    </div>
                  </div>
                  <div className={styles.rowMeta}>
                    <Badge variant={STATUS_VARIANT[topic.status] || 'default'} size="sm">
                      {STATUS_LABEL[topic.status] || topic.status}
                    </Badge>
                    <Badge variant={PRIORITY_VARIANT[topic.priority] || 'default'} size="sm">
                      {(topic.priority || '').toLowerCase() || 'medium'}
                    </Badge>
                  </div>
                </RowEl>
              </li>
            );
          })}
        </ul>
      )}
    </Card>
  );
}

export default TopicsPanel;
